import type { ComponentType } from 'react';
import React from 'react';

import type {
  DebugMode,
  GeneralContext,
  Platform,
  RicosPortal,
  RicosServices,
  RicosTheme,
  TFunction,
} from './../ricos-types';

type RicosContextType = GeneralContext & { theme: RicosTheme };

export const RicosContext = React.createContext<RicosContextType>({
  locale: 'en',
  localeContent: 'en',
  experiments: {},
  isMobile: false,
  t: ((text: string) => text) as TFunction,
  languageDir: 'ltr',
  theme: {} as RicosTheme,
  portal: null,
  debugMode: [],
  platform: 'macOs',
  content: null as unknown as RicosServices['content'],
  jsdomMode: false,
  environment: 'liveSite',
  tabIndex: 0,
});

export const RicosContextProvider = ({
  children,
  isMobile,
  locale,
  localeContent,
  experiments,
  languageDir,
  theme,
  t,
  portal,
  debugMode = [] as DebugMode[],
  jsdomMode = false,
  platform = 'macOs' as Platform,
  content = null as unknown as RicosServices['content'],
  environment = 'liveSite',
  tabIndex = 0,
}: {
  children: React.ReactNode;
  isMobile: boolean;
  locale: string;
  localeContent: string;
  experiments?: GeneralContext['experiments'];
  languageDir: GeneralContext['languageDir'];
  theme: RicosTheme;
  t: TFunction;
  portal?: RicosPortal;
  debugMode?: DebugMode[];
  jsdomMode?: boolean;
  platform?: Platform;
  content?: RicosServices['content'];
  environment?: GeneralContext['environment'];
  tabIndex?: number;
}) => {
  return (
    <RicosContext.Provider
      value={{
        t,
        locale,
        localeContent,
        isMobile,
        experiments: experiments ?? {},
        languageDir,
        theme,
        portal: portal ?? null,
        debugMode,
        jsdomMode,
        platform,
        content,
        environment,
        tabIndex,
      }}
    >
      {children}
    </RicosContext.Provider>
  );
};

export function withRicosContext<T = any>(Component: ComponentType<T>) {
  return (props: T) => {
    return (
      <RicosContext.Consumer>
        {(value: GeneralContext) => <Component {...props} ricosContext={value} />}
      </RicosContext.Consumer>
    );
  };
}

export const RicosContextConsumer = ({ children }: $TSFixMe) => {
  return (
    <RicosContext.Consumer>{(value: GeneralContext) => children(value)}</RicosContext.Consumer>
  );
};
